import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import api from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { 
  Flame, Droplets, Thermometer, Clock,
  Plus, Play, Pause, CheckCircle, AlertTriangle,
  Package, Beaker, Scale, FileText,
  Timer, Activity, Wind, Factory,
  Bot, Send, MessageSquare, Settings
} from 'lucide-react';

export default function AgentDistillateurPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('runs');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [runs, setRuns] = useState([]);
  const [sites, setSites] = useState([]);
  const [equipements, setEquipements] = useState([]);
  const [lots, setLots] = useState([]);
  const [recettes, setRecettes] = useState([]);
  const [stats, setStats] = useState(null);
  const [runActif, setRunActif] = useState(null);
  
  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  
  // Dialog states
  const [showNewRun, setShowNewRun] = useState(false);
  const [showNewSite, setShowNewSite] = useState(false);
  
  // Form states
  const [newRun, setNewRun] = useState({
    site_id: '',
    recette_id: '',
    plante: '',
    masse_kg: 100,
    etat_matiere: 'fraiche'
  });

  useEffect(() => {
    loadData();
    // Poll for active run
    const interval = setInterval(() => {
      if (runActif?.statut === 'en_cours') {
        loadRunActif();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, runsRes, sitesRes, lotsRes, recettesRes] = await Promise.all([
        api.get('/distillateur/stats').catch(() => ({ data: null })),
        api.get('/distillateur/runs').catch(() => ({ data: [] })),
        api.get('/distillateur/sites').catch(() => ({ data: [] })),
        api.get('/distillateur/lots').catch(() => ({ data: [] })),
        api.get('/distillateur/recettes').catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      setRuns(Array.isArray(runsRes.data) ? runsRes.data : []);
      setSites(Array.isArray(sitesRes.data) ? sitesRes.data : []);
      setLots(Array.isArray(lotsRes.data) ? lotsRes.data : []);
      setRecettes(Array.isArray(recettesRes.data) ? recettesRes.data : []);
      
      // Trouver le run actif
      const actif = (runsRes.data || []).find(r => r.statut === 'en_cours' || r.statut === 'preparation');
      setRunActif(actif);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRunActif = async () => {
    if (!runActif?.id) return;
    try {
      const res = await api.get(`/api/distillateur/runs/${runActif.id}`);
      setRunActif(res.data);
    } catch (error) {
      console.error('Erreur refresh run:', error);
    }
  };

  const handleChat = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setChatLoading(true);
    
    try {
      const response = await api.post('/distillateur/chat', { message: chatInput });
      const assistantMessage = { role: 'assistant', content: response.data.response };
      setChatMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      toast.error("Impossible de communiquer avec l'agent");
    } finally {
      setChatLoading(false);
    }
  };

  const startRun = async () => {
    try {
      const res = await api.post('/distillateur/runs', newRun);
      toast.success("Distillation démarrée");
      setShowNewRun(false);
      setNewRun({ site_id: '', recette_id: '', plante: '', masse_kg: 100, etat_matiere: 'fraiche' });
      loadData();
    } catch (error) {
      toast.error("Impossible de démarrer");
    }
  };

  const updateRunStatus = async (runId, newStatus) => {
    try {
      await api.patch(`/api/distillateur/runs/${runId}`, { statut: newStatus });
      toast.success(`Statut mis à jour: ${newStatus}`);
      loadData();
    } catch (error) {
      toast.error("Impossible de mettre à jour");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      preparation: 'bg-yellow-500',
      en_cours: 'bg-blue-500',
      plateau: 'bg-green-500',
      refroidissement: 'bg-cyan-500',
      termine: 'bg-emerald-500',
      incident: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getProgress = (run) => {
    if (!run?.start_ts) return 0;
    const start = new Date(run.start_ts).getTime();
    const now = Date.now();
    const durationMin = run.duree_estimee_min || 180;
    const elapsed = (now - start) / 60000;
    return Math.min(100, (elapsed / durationMin) * 100);
  };

  return (
    <div className="p-6 space-y-6" data-testid="agent-distillateur-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg">
              <Flame className="h-8 w-8 text-white" />
            </div>
            Agent Distillateur
          </h1>
          <p className="text-muted-foreground mt-1">Pilotage distillation HE & Hydrolats</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => navigate('/agents/config', { state: { selectedAgent: 'technique' } })}
            data-testid="agent-settings-btn"
          >
            <Settings className="h-4 w-4 mr-2" />
            Paramètres
          </Button>
          <Button variant="outline" onClick={() => setShowNewSite(true)}>
            <Factory className="h-4 w-4 mr-2" />
            Nouveau Site
          </Button>
          <Button onClick={() => setShowNewRun(true)} className="bg-orange-500 hover:bg-orange-600">
            <Play className="h-4 w-4 mr-2" />
            Nouvelle Distillation
          </Button>
        </div>
      </div>

      {/* Run Actif Panel */}
      {runActif && (
        <Card className="border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-orange-500 animate-pulse" />
                Distillation en cours
              </CardTitle>
              <Badge className={getStatusColor(runActif.statut)}>{runActif.statut}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
              <div>
                <p className="text-sm text-muted-foreground">Plante</p>
                <p className="font-semibold">{runActif.plante || 'Lavande'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Masse</p>
                <p className="font-semibold">{runActif.masse_kg || 0} kg</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Température</p>
                <p className="font-semibold flex items-center gap-1">
                  <Thermometer className="h-4 w-4 text-red-500" />
                  {runActif.temperature_actuelle || '--'}°C
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pression</p>
                <p className="font-semibold">{runActif.pression_bar || '--'} bar</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">HE collectée</p>
                <p className="font-semibold flex items-center gap-1">
                  <Droplets className="h-4 w-4 text-amber-500" />
                  {runActif.he_collectee_ml || 0} mL
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progression</span>
                <span>{Math.round(getProgress(runActif))}%</span>
              </div>
              <Progress value={getProgress(runActif)} className="h-2" />
            </div>
            <div className="flex gap-2 mt-4">
              {runActif.statut === 'preparation' && (
                <Button size="sm" onClick={() => updateRunStatus(runActif.id, 'en_cours')}>
                  <Play className="h-4 w-4 mr-1" /> Démarrer
                </Button>
              )}
              {runActif.statut === 'en_cours' && (
                <>
                  <Button size="sm" variant="outline" onClick={() => updateRunStatus(runActif.id, 'plateau')}>
                    Phase Plateau
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => updateRunStatus(runActif.id, 'incident')}>
                    <AlertTriangle className="h-4 w-4 mr-1" /> Incident
                  </Button>
                </>
              )}
              {(runActif.statut === 'plateau' || runActif.statut === 'refroidissement') && (
                <Button size="sm" onClick={() => updateRunStatus(runActif.id, 'termine')}>
                  <CheckCircle className="h-4 w-4 mr-1" /> Terminer
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">Distillations</p>
                <p className="text-2xl font-bold">{stats?.total_runs || runs.length}</p>
              </div>
              <Flame className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">HE produite (L)</p>
                <p className="text-2xl font-bold">{stats?.total_he_litres || 0}</p>
              </div>
              <Droplets className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">Hydrolats (L)</p>
                <p className="text-2xl font-bold">{stats?.total_hydrolat_litres || 0}</p>
              </div>
              <Beaker className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">Lots QC OK</p>
                <p className="text-2xl font-bold text-green-500">{stats?.lots_qc_ok || 0}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">Rendement moy.</p>
                <p className="text-2xl font-bold">{stats?.rendement_moyen || '--'}%</p>
              </div>
              <Scale className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Tabs Content */}
        <div className="lg:col-span-2">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="runs">Distillations</TabsTrigger>
              <TabsTrigger value="lots">Lots produits</TabsTrigger>
              <TabsTrigger value="sites">Sites</TabsTrigger>
              <TabsTrigger value="recettes">Recettes</TabsTrigger>
            </TabsList>

            <TabsContent value="runs" className="space-y-4 mt-4">
              {runs.length === 0 ? (
                <Card className="p-8 text-center">
                  <Flame className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucune distillation enregistrée.</p>
                  <Button className="mt-4" onClick={() => setShowNewRun(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Démarrer une distillation
                  </Button>
                </Card>
              ) : (
                <div className="space-y-3">
                  {runs.map((run) => (
                    <Card key={run.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{run.plante || 'Distillation'}</h3>
                              <Badge className={getStatusColor(run.statut)}>{run.statut}</Badge>
                            </div>
                            <div className="grid grid-cols-3 gap-4 mt-2 text-sm text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Scale className="h-3 w-3" />
                                {run.masse_kg} kg
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {run.duree_min || '--'} min
                              </div>
                              <div className="flex items-center gap-1">
                                <Droplets className="h-3 w-3" />
                                {run.he_produite_ml || 0} mL HE
                              </div>
                            </div>
                            {run.start_ts && (
                              <p className="text-xs text-muted-foreground mt-2">
                                Début: {new Date(run.start_ts).toLocaleString('fr-FR')}
                              </p>
                            )}
                          </div>
                          <Button variant="ghost" size="sm">Détails</Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="lots" className="space-y-4 mt-4">
              {lots.length === 0 ? (
                <Card className="p-8 text-center">
                  <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucun lot produit.</p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {lots.map((lot) => (
                    <Card key={lot.id}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium">{lot.numero_lot || lot.id?.slice(0,8)}</h4>
                            <p className="text-sm text-muted-foreground">{lot.type_produit}</p>
                            <div className="flex gap-2 mt-2">
                              <Badge variant="outline">{lot.volume_l || 0} L</Badge>
                              <Badge className={lot.statut_qc === 'ok' ? 'bg-green-500' : 'bg-yellow-500'}>
                                QC: {lot.statut_qc || 'en attente'}
                              </Badge>
                            </div>
                          </div>
                          <Droplets className={`h-6 w-6 ${lot.type_produit === 'huile_essentielle' ? 'text-amber-500' : 'text-blue-500'}`} />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="sites" className="space-y-4 mt-4">
              {sites.length === 0 ? (
                <Card className="p-8 text-center">
                  <Factory className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucun site de distillation.</p>
                  <Button className="mt-4" onClick={() => setShowNewSite(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter un site
                  </Button>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {sites.map((site) => (
                    <Card key={site.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${site.type === 'mobile' ? 'bg-blue-100' : 'bg-orange-100'}`}>
                            <Factory className={`h-5 w-5 ${site.type === 'mobile' ? 'text-blue-600' : 'text-orange-600'}`} />
                          </div>
                          <div>
                            <h4 className="font-medium">{site.nom}</h4>
                            <p className="text-sm text-muted-foreground">{site.adresse || site.type}</p>
                            <Badge variant="outline" className="mt-2">{site.type || 'fixe'}</Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="recettes" className="space-y-4 mt-4">
              {recettes.length === 0 ? (
                <Card className="p-8 text-center">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucune recette de distillation.</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {recettes.map((recette) => (
                    <Card key={recette.id}>
                      <CardContent className="p-4">
                        <div className="flex justify-between">
                          <div>
                            <h4 className="font-medium">{recette.nom}</h4>
                            <p className="text-sm text-muted-foreground">{recette.plante}</p>
                            <div className="flex gap-2 mt-2 text-xs">
                              <span>Durée: {recette.duree_min} min</span>
                              <span>Temp: {recette.temperature_cible}°C</span>
                            </div>
                          </div>
                          <Badge>{recette.statut || 'active'}</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Right: Chat Assistant */}
        <Card className="h-[600px] flex flex-col">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Bot className="h-5 w-5 text-orange-500" />
              Assistant Distillation
            </CardTitle>
            <CardDescription>Conseils et aide au pilotage</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            <div className="flex-1 overflow-y-auto space-y-3 mb-4 p-2 bg-muted/30 rounded-lg">
              {chatMessages.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Posez vos questions sur la distillation, les paramètres optimaux, le dépannage...</p>
                </div>
              ) : (
                chatMessages.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] p-3 rounded-lg ${
                      msg.role === 'user' 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-background border'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))
              )}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-background border p-3 rounded-lg">
                    <div className="flex gap-1">
                      <span className="animate-bounce">.</span>
                      <span className="animate-bounce" style={{ animationDelay: '0.1s' }}>.</span>
                      <span className="animate-bounce" style={{ animationDelay: '0.2s' }}>.</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Input 
                placeholder="Posez votre question..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleChat()}
              />
              <Button onClick={handleChat} disabled={chatLoading} className="bg-orange-500 hover:bg-orange-600">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dialog: Nouvelle Distillation */}
      <Dialog open={showNewRun} onOpenChange={setShowNewRun}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle Distillation</DialogTitle>
            <DialogDescription>Démarrez une nouvelle session de distillation</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Site de distillation</Label>
              <Select 
                value={newRun.site_id}
                onValueChange={(v) => setNewRun({...newRun, site_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un site" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map(site => (
                    <SelectItem key={site.id} value={site.id}>{site.nom}</SelectItem>
                  ))}
                  <SelectItem value="default">Site principal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Plante</Label>
                <Input 
                  value={newRun.plante}
                  onChange={(e) => setNewRun({...newRun, plante: e.target.value})}
                  placeholder="Lavande, Thym, Romarin..."
                />
              </div>
              <div className="space-y-2">
                <Label>Masse (kg)</Label>
                <Input 
                  type="number"
                  value={newRun.masse_kg}
                  onChange={(e) => setNewRun({...newRun, masse_kg: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Etat de la matière</Label>
              <Select 
                value={newRun.etat_matiere}
                onValueChange={(v) => setNewRun({...newRun, etat_matiere: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fraiche">Fraîche</SelectItem>
                  <SelectItem value="prefanee">Préfanée</SelectItem>
                  <SelectItem value="seche">Sèche</SelectItem>
                  <SelectItem value="mouillee">Mouillée</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {recettes.length > 0 && (
              <div className="space-y-2">
                <Label>Recette (optionnel)</Label>
                <Select 
                  value={newRun.recette_id}
                  onValueChange={(v) => setNewRun({...newRun, recette_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Aucune recette" />
                  </SelectTrigger>
                  <SelectContent>
                    {recettes.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.nom}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewRun(false)}>Annuler</Button>
            <Button onClick={startRun} className="bg-orange-500 hover:bg-orange-600">
              <Play className="h-4 w-4 mr-2" />
              Démarrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
